import './policy-manager-actions.scss';
import React, { useMemo, useState } from 'react';
import CustomIcon, { IconType } from 'src/components/shared/custom-icon/custom-icon';
import { DisableReasons, PMActionType, PolicyType, VersionState } from '../../../../utils/enums';
import { useStores } from '../../../store';
import { useNavigate } from 'src/utils/use-navigate';
import { DRAFT_STATUSES, isVersionActive, isVersionInactive, PENDING_STATUSES } from '../../../../utils/versionUtils';
import { FwVersionInfo } from '../../../../dtos/version.dto';
import { ServiceDto } from '../../../../dtos/customer.dto';
import { TooltipDisableReason } from '../../../shared/tooltip-disable-message/tooltip-disable-reason';
import { RestorePolicy } from '../../restore-policy-modal/restore-policy';
import { observer } from 'mobx-react-lite';
import { RemoveVersionModal } from './action-modals/remove-version-modal';
import { PullPolicyModal } from './action-modals/pull-policy-modal';
import { PolicyActionDto, PolicyActionModal } from '../../version-button/policy-action-modal';
import usePromiseFeedback from '../../../shared/promise-feedback/usePromiseFeedback';
import fwVersionService from '../../../../services/fw-version.service';
import { EventType } from '../../../../dtos/analytics.dto';
import { PushPolicyModal } from './action-modals/push-policy-modal';
import { DeactivateVersion } from '../../../shared/deactivate-version/deactivate-version';
import MoreIcon from '@svg/more.svg?react';
import PolicyStatusDisplay from '../../../shared/policy-status-display/policy-status-display';
import { isInitialPolicyModeUndefinedByService } from '../../ecm/connection.model';

const COPY_POLICY_LABEL = 'Copy Policy';

interface PolicyManagerActionsProps {
    selectedConnection: ServiceDto | undefined;
    selectedVersion: FwVersionInfo | undefined;
    closePanel: () => void;
}

export const PolicyManagerActions: React.FC<PolicyManagerActionsProps> = observer(({selectedConnection, selectedVersion, closePanel}) => {
    const {fwVersionStore, customerStore, userStore} = useStores();
    const navigate = useNavigate();
    const [showRestorePolicyModal, setShowRestorePolicyModal] = useState(false);
    const [showRemovePolicyModal, setShowRemovePolicyModal] = useState(false);
    const [showPullPolicyModal, setShowPullPolicyModal] = useState(false);
    const [showPushPolicyModal, setShowPushPolicyModal] = useState(false);
    const [showEditPolicyModal, setShowEditPolicyModal] = useState(false);
    const [promiseFeedbackFactory, promiseContextHolder] = usePromiseFeedback();

    const isCurrentPolicy = useMemo(() => selectedVersion?.externalId === fwVersionStore.selectedFwVersion?.externalId,
        [selectedVersion, fwVersionStore.selectedFwVersion]);

    const isCurrentConnection: boolean = useMemo(() => {
        return selectedConnection?.serviceId === customerStore.selectedLocation?.serviceId;
    },[selectedConnection, customerStore.selectedLocation]);

    const currentConnectionActionsList = (selectedVersion: FwVersionInfo): PMActionType[] => {
        const baseActions = [PMActionType.VIEW, PMActionType.EDIT];
        if (DRAFT_STATUSES.includes(selectedVersion.versionStatus)) return [...baseActions, PMActionType.DELETE];
        if (isVersionActive(selectedVersion)) return [...baseActions, PMActionType.PUSH];
        if (PENDING_STATUSES.includes(selectedVersion.versionStatus) ||
            selectedVersion.versionState === VersionState.ACTIVE) return baseActions;
        if (isVersionInactive(selectedVersion)) return [...baseActions, PMActionType.RESTORE];
        return [];
    };

    const diffConnectionActionsList = (selectedVersion: FwVersionInfo): PMActionType[] => {
        if (selectedVersion.versionState === VersionState.ACTIVE) return [PMActionType.VIEW, PMActionType.PULL];
        if (PENDING_STATUSES.includes(selectedVersion.versionStatus)) return [PMActionType.VIEW];
        return [];
    };

    const actionsList: PMActionType[] = useMemo(() => {
        if (!selectedVersion)  return [];
        return isCurrentConnection ? currentConnectionActionsList(selectedVersion) :  diffConnectionActionsList(selectedVersion);
    }, [isCurrentConnection, selectedVersion]);

    const hasPendingVersionOnSelectedConnection = useMemo(() => {
        if (!selectedConnection) return false;
        return fwVersionStore.allFwVersions[selectedConnection?.serviceId]?.some(policy => PENDING_STATUSES.includes(policy.versionStatus));
    }, [selectedConnection, fwVersionStore.allFwVersions]);

    const disableReasonPull = useMemo(()=> {
        if (!selectedVersion || !selectedConnection) return '';
        if (PENDING_STATUSES.includes(selectedVersion.versionStatus)) return DisableReasons.PULL_NOT_AVAILABLE_PENDING;
        if (hasPendingVersionOnSelectedConnection) return DisableReasons.PULL_NOT_AVAILABLE_PENDING;
        if (selectedVersion.versionNumber === 1 && selectedVersion.versionState === VersionState.ACTIVE) return DisableReasons.PULL_NOT_AVAILABLE_BASE_VERSION;
        if (selectedVersion?.versionType === PolicyType.DEACTIVATE) return DisableReasons.PULL_NOT_AVAILABLE_FROM_DEACTIVATED;
        return '';
    }, [selectedVersion, hasPendingVersionOnSelectedConnection]);

    const disableReasonPush = useMemo(()=> {
        if (!selectedVersion) return '';
        if (PENDING_STATUSES.includes(selectedVersion.versionStatus)) return DisableReasons.PUSH_NOT_AVAILABLE_PENDING;
        if (DRAFT_STATUSES.includes(selectedVersion.versionStatus)) return DisableReasons.PUSH_NOT_AVAILABLE_DRAFT_VERSION;
        if (selectedVersion.versionNumber === 1 && selectedVersion.versionState === VersionState.ACTIVE) return DisableReasons.PUSH_NOT_AVAILABLE_BASE_VERSION;
        if (selectedVersion?.versionType === PolicyType.DEACTIVATE) return DisableReasons.PUSH_NOT_AVAILABLE_FROM_DEACTIVATED;
        return '';
    }, [selectedVersion]);

    const deactivateReasonRestore = useMemo(() => {
        if (!selectedVersion) return '';
        if (selectedVersion?.versionType === PolicyType.DEACTIVATE) return DisableReasons.RESTORE_NOT_AVAILABLE_FROM_DEACTIVATED;
        return '';
    }, [selectedVersion]);

    const scheduledVersionSource = useMemo(() =>
        customerStore.isSourceScheduledVersion ? DisableReasons.SCHEDULED_VERSION_EXITS : '', [customerStore.isSourceScheduledVersion]);

    const scheduledVersionTargetUpcoming = useMemo(() =>
        customerStore.isTargetScheduledVersion && customerStore.isScheduledVersionUpcoming ?
            DisableReasons.SCHEDULED_VERSION_COME_ON : '', [customerStore.isScheduledVersionUpcoming, customerStore.isTargetScheduledVersion]);

    const pendingDisableReason = useMemo(()=>fwVersionStore.pendingVersion ? DisableReasons.PENDING_EXIST : '',[fwVersionStore.pendingVersion]);
    const readonlyDisableReason = useMemo(()=> userStore.isReadOnlyUser ? DisableReasons.READ_ONLY_USER : '', [userStore.isReadOnlyUser]);
    const smartInProgressDisableReason = useMemo(()=>
        customerStore.isSmartDraftCreationInProgress ? DisableReasons.SMART_DRAFT_IN_PROGRESS : '', [customerStore.isSmartDraftCreationInProgress]);

    const actionsMap :Record<PMActionType, {icon: IconType, label: string, onClick: () => void, disableReason?: string, hide?: boolean }> =useMemo(()=> {
        return{
        [PMActionType.VIEW]: {
            icon: IconType.VIEW_POLICY_ICON,
            label: PMActionType.VIEW,
            onClick: async () => {
                fwVersionStore.sendGeneralEvent(EventType.LinkClicked, { button: 'viewPolicy' });
                if(!isCurrentPolicy) {
                    if (selectedConnection && isInitialPolicyModeUndefinedByService(selectedConnection)) {
                        await customerStore.setCustomPolicy([selectedConnection.serviceId]);
                    }
                    const serviceId = isCurrentConnection ? customerStore.serviceId : selectedConnection?.serviceId;
                    const path = `/${customerStore.selectedCustomer?.customerId}/${serviceId}/protection/version/${selectedVersion?.externalId}/${
                        fwVersionStore.versionProtectionPath}`;
                    navigate(path, { replace: true });
                }
                closePanel();
            }
        },
        [PMActionType.EDIT]: {
            icon: IconType.PENCIL,
            label: PMActionType.EDIT,
            onClick: () => setShowEditPolicyModal(true),
            disableReason: readonlyDisableReason || smartInProgressDisableReason
        },
        [PMActionType.DELETE]: {
            icon: IconType.TRASH,
            label: PMActionType.DELETE,
            onClick: () => setShowRemovePolicyModal(true),
            disableReason: readonlyDisableReason || smartInProgressDisableReason
        },
        [PMActionType.RESTORE]: {
            icon: IconType.RESTORE,
            label: PMActionType.RESTORE,
            onClick: () => setShowRestorePolicyModal(true),
            disableReason: readonlyDisableReason || scheduledVersionSource ||
                (!fwVersionStore.isVersionDeactivate && fwVersionStore.getDisableReason !== DisableReasons.INACTIVE_VERSION
                    && fwVersionStore.getDisableReason) ||
                pendingDisableReason || deactivateReasonRestore || smartInProgressDisableReason
        },
        [PMActionType.PULL]: {
            icon: IconType.PULL_POLICY_ICON,
            label: COPY_POLICY_LABEL,
            onClick: () => setShowPullPolicyModal(true),
            disableReason: readonlyDisableReason || pendingDisableReason || smartInProgressDisableReason
                || disableReasonPull || scheduledVersionTargetUpcoming || scheduledVersionSource
        },
        [PMActionType.PUSH]: {
            icon: IconType.PUSH_POLICY_ICON,
            label: COPY_POLICY_LABEL,
            hide: customerStore.isOneConnectionNonBronze,
            onClick: () => {
                fwVersionStore.sendGeneralEvent(EventType.LinkClicked, { button: 'pushPolicy', location: 'policyManager' });
                setShowPushPolicyModal(true);
            },
            disableReason: readonlyDisableReason || pendingDisableReason || smartInProgressDisableReason || disableReasonPush
        }
    };},[selectedVersion, pendingDisableReason, disableReasonPull, readonlyDisableReason]) ;

    const editPolicy = (data: PolicyActionDto)=> {
        const { policyName, policyComments, externalId } = data;
        if(!externalId) return;
        fwVersionStore.sendGeneralEvent(EventType.LinkClicked, {
            button: 'save',
            location: 'updateInfoModal'
        });
        promiseFeedbackFactory({
            promise: fwVersionStore.genericVersionActionMethod([externalId, policyComments, policyName], fwVersionService.updateVersionInfo ),
            notifObj: { show: true }
        });
    };

    return <div className="policy-manager-actions">
        <RestorePolicy open={showRestorePolicyModal} close={() => setShowRestorePolicyModal(false)} selectedVersionData={selectedVersion} closePanel={closePanel}/>
        <RemoveVersionModal open={showRemovePolicyModal} setOpen={setShowRemovePolicyModal} versionExternalId={selectedVersion?.externalId ?? ''}/>
        <PullPolicyModal open={showPullPolicyModal} close={() => setShowPullPolicyModal(false)}
                         sourceCircuitId={selectedConnection?.serviceName || selectedConnection?.adiCircuitId} versionData={selectedVersion} closePanel={closePanel}/>
        <PushPolicyModal open={showPushPolicyModal} close={() => setShowPushPolicyModal(false)}
                         sourceCircuitId={selectedConnection?.serviceName || selectedConnection?.adiCircuitId} versionData={selectedVersion} closePanel={closePanel}/>
        <PolicyActionModal visible={showEditPolicyModal}
                           close={() => setShowEditPolicyModal(false)}
                           ok={editPolicy}
                           title={'Update Info'}
                           okBtnLabel={'Save'}
                           initialData={{
                               policyName: selectedVersion?.versionName,
                               policyComments: selectedVersion?.comments,
                               externalId: selectedVersion?.externalId
                           }}
                           className={'update-info-modal'}
                           >
            {selectedVersion ?
                <PolicyStatusDisplay
                    policyStatus={selectedVersion?.versionStatus}
                    policyNumber={selectedVersion?.versionNumber}
                    policyState={selectedVersion?.versionState}/> :
                <></>}
        </PolicyActionModal>
        {actionsList?.map(action =>
             !actionsMap[action]?.hide &&
                <TooltipDisableReason disableReason={actionsMap[action]?.disableReason}  key={actionsMap[action].label}
                                      overlayClassName="z-index-higher-pm">
                    <button type="button" className={`action-btn ${actionsMap[action]?.disableReason ? 'disabled' : ''}`} aria-disabled={!!actionsMap[action]?.disableReason}
                            onClick={actionsMap[action].onClick} key={actionsMap[action].label}>
                        <CustomIcon type={actionsMap[action].icon} size={24}/>
                        {actionsMap[action].label}
                    </button>
                </TooltipDisableReason>
        )}
        {!userStore.isReadOnlyUser && customerStore.isAvailable && isCurrentConnection && !fwVersionStore.pendingVersion
            && <DeactivateVersion protectionType={'DYNAMIC_DEFENSE'} popoverIcon={<MoreIcon/>} closePanel={closePanel} />}
        {promiseContextHolder}
    </div>;
});
